import { NavigateOptions } from 'react-router-dom';
import { Grid, Pagination as PaginationBar, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import {
	PagingConfig,
	UserRole,
	Pagination,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { FunctionaryCard } from './FunctionaryCard';
import { DEFAULT_PAGE_SIZE } from '../../../shared/Constants';
import { FunctionaryTableView } from './FunctionariesTableList';
import useUserSession from '../../../hooks/useUserSession';
import { PositionAssignmentFilters } from './FilterFunctionaries';
import EmptyList from '../../../components/EmptyList';
import { PositionAssignmentUser } from '../../../shared/MySrcm';

const { useBreakpoint } = Grid;

interface FunctionariesListProps {
	data: {
		functionary: PositionAssignmentUser[];
		pagination: Pagination;
	};

	onPageChange: (page: number, pageSize: number) => void;

	onEditClick: (functionary: PositionAssignmentUser) => void;

	pageConfig: PagingConfig;

	navigateWithFunctionarySrcmId: (
		route: string,
		functionarySIdParam?: string,
		params?: NavigateOptions
	) => void;

	onFilterChange: (filters: PositionAssignmentFilters) => void;
	loading: boolean;
	isApiFetched: boolean;
}

export const FunctionariesList = ({
	data: { functionary, pagination },
	onPageChange,
	onEditClick,
	pageConfig,
	navigateWithFunctionarySrcmId,
	onFilterChange,
	loading,
	isApiFetched,
}: FunctionariesListProps) => {
	const screens = useBreakpoint();
	const { selectedRole } = useUserSession();
	const { t } = useTranslation();
	const isMobile = !screens.sm;

	if (loading) {
		return (
			<div className="flex justify-center items-center min-h-[300px]">
				<Spin size="small" />
			</div>
		);
	}

	if (
		isApiFetched &&
		(pagination.totalNoOfRecords === null || pagination.totalNoOfRecords === 0)
	) {
		return (
			<EmptyList
				message={
					selectedRole === UserRole.SNP_ADMIN
						? t('noFunctionaries')
						: t('noProposers')
				}
			/>
		);
	}

	const proposerCards = functionary?.map((proposer) => (
		<div className="mb-[16px]" key={proposer.partner_id}>
			<FunctionaryCard {...proposer} onEditClick={onEditClick} />{' '}
		</div>
	));

	return (
		<div className="flex flex-col gap-6">
			{isMobile ? (
				<>
					{proposerCards}
					<div className="flex justify-center w-full">
						<PaginationBar
							current={pageConfig.pageNo ?? 1}
							total={pagination.totalNoOfRecords}
							showTotal={(total, range) =>
								`${range[0]}-${range[1]} of ${total} items`
							}
							defaultPageSize={pageConfig.pageSize ?? DEFAULT_PAGE_SIZE}
							onChange={onPageChange}
						/>
					</div>
				</>
			) : (
				<FunctionaryTableView
					functionaries={functionary}
					pagination={pagination}
					paging={pageConfig}
					onPageChange={onPageChange}
					navigateWithFunctionarySrcmId={navigateWithFunctionarySrcmId}
					onFilterChange={onFilterChange}
				/>
			)}
		</div>
	);
};
